import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listBundleSkus, listKitComponents } from "@/lib/inventory-catalog.functions";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Eye } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/inventory/bundles")({ component: BundlesPage });

function BundlesPage() {
  const { tenantId, customerId } = useScope();
  const listBundlesFn = useServerFn(listBundleSkus);
  const [viewId, setViewId] = useState<string | null>(null);

  const { data: bundles = [], isLoading } = useQuery({
    queryKey: ["bundle-skus", tenantId, customerId],
    enabled: !!tenantId,
    queryFn: () => listBundlesFn({ data: { tenantId: tenantId!, customerId: customerId ?? null } }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Bundles</h2>
        <p className="text-sm text-muted-foreground">Bundles are sold as a group of component SKUs. They hold no inventory of their own — when ordered, each component is picked and shipped individually. Toggle "Bundle (sold as group)" on a SKU and edit its components from the SKUs page.</p>
      </div>
      <div className="rounded-md border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium">Bundle Catalog</h3>
          <Badge variant="outline">{bundles.length} bundles</Badge>
        </div>
        {isLoading ? (
          <div className="p-8 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Name</TableHead><TableHead className="text-right">Components</TableHead></TableRow></TableHeader>
            <TableBody>
              {bundles.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">No bundle SKUs. Toggle "Bundle (sold as group)" on a SKU to add one.</TableCell></TableRow>}
              {bundles.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-sm">{s.sku_code}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setViewId(s.id)}><Eye className="h-4 w-4 mr-1" /> View components</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {viewId && <BundleViewDialog bundleId={viewId} bundle={bundles.find((b) => b.id === viewId) ?? null} onClose={() => setViewId(null)} />}
    </div>
  );
}

function BundleViewDialog({ bundleId, bundle, onClose }: { bundleId: string; bundle: { id: string; sku_code: string; name: string } | null; onClose: () => void }) {
  const listComponentsFn = useServerFn(listKitComponents);
  const { data: comps = [] } = useQuery({
    queryKey: ["kit-components", bundleId],
    queryFn: () => listComponentsFn({ data: { kitId: bundleId } }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Components — {bundle?.sku_code} {bundle?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Table>
            <TableHeader><TableRow><TableHead>Component</TableHead><TableHead>Qty / bundle</TableHead></TableRow></TableHeader>
            <TableBody>
              {comps.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">No components defined. Edit this bundle on the SKUs page.</TableCell></TableRow>}
              {comps.map((c: any) => (
                <TableRow key={c.id}>
                  <TableCell><span className="font-mono text-sm">{c.skus?.sku_code}</span> <span className="text-muted-foreground">{c.skus?.name}</span></TableCell>
                  <TableCell>{c.qty_per_kit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">To edit components, go to the SKUs page and open this bundle.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}